import checkboxStyles from '@/components/UI/Checkbox.module.css'
import { FileButton } from '@/components/UI/FileButton'
import { Radio } from '@/components/UI/Radio'
import type { QuestionnaireAnswerFormData } from '@/schemas/forms/questionnaire'
import type { QuestionnaireItemData } from '@/schemas/v2/questionnaire'
import { Checkbox, Radio as MantineRadioGroup, MultiSelect, Select, TextInput, Textarea } from '@mantine/core'
import { memo, useState } from 'react'
import type { CSSProperties } from 'react'
import { type Control, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type Props = {
  item: QuestionnaireItemData
  control: Control<QuestionnaireAnswerFormData>
}

// formType=8 + demographic=age 専用の内部コンポーネント（年・月を個別 SelectBox で入力→ YYYYMM に結合）
type YearMonthFieldProps = {
  label: string
  required: boolean
  value: string // "YYYYMM" 形式、未入力は ''
  onChange: (value: string) => void
  error?: string
}

const currentYear = new Date().getFullYear()
const yearOptions = Array.from({ length: currentYear - 1919 }, (_, i) => {
  const y = String(currentYear - i)
  return { label: `${y}年`, value: y }
})
const monthOptions = Array.from({ length: 12 }, (_, i) => {
  const m = String(i + 1).padStart(2, '0')
  return { label: `${i + 1}月`, value: m }
})

const QuestionnaireYearMonthField = memo(({ label, required, value, onChange, error }: YearMonthFieldProps) => {
  const { t } = useTranslation()
  // 年・月をローカル state で保持する。
  // value (YYYYMM) を直接 Select の value に使うと、
  // 年だけ選択した時点で month='' → onChange('') が呼ばれ value がリセットされるため。
  const [localYear, setLocalYear] = useState(value.length >= 4 ? value.slice(0, 4) : '2000')
  const [localMonth, setLocalMonth] = useState(value.length === 6 ? value.slice(4, 6) : '01')

  const handleYearChange = (y: string) => {
    setLocalYear(y)
    onChange(y && localMonth ? `${y}${localMonth}` : '')
  }
  const handleMonthChange = (m: string) => {
    setLocalMonth(m)
    onChange(localYear && m ? `${localYear}${m}` : '')
  }

  return (
    <div>
      <div className="text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </div>
      <div className="flex gap-3">
        <Select
          data={yearOptions}
          value={localYear || null}
          onChange={(val) => handleYearChange(val ?? '')}
          placeholder={t('register_questionnaire.year_placeholder', '年を選択')}
          aria-label={t('register_questionnaire.year_label', '年')}
          allowDeselect={false}
          inputMode="none"
          styles={{ input: { fontSize: '16px' } as CSSProperties }}
        />
        <Select
          data={monthOptions}
          value={localMonth || null}
          onChange={(val) => handleMonthChange(val ?? '')}
          placeholder={t('register_questionnaire.month_placeholder', '月を選択')}
          aria-label={t('register_questionnaire.month_label', '月')}
          allowDeselect={false}
          inputMode="none"
          styles={{ input: { fontSize: '16px' } as CSSProperties }}
        />
      </div>
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  )
})

QuestionnaireYearMonthField.displayName = 'QuestionnaireYearMonthField'

export const QuestionnaireFormField = memo(({ item, control }: Props) => {
  const { t } = useTranslation()
  const key = String(item.id)
  const label = item.question ?? ''
  const required = item.isRequire ?? false
  const options = item.formValue ?? []

  const defaultValue = (() => {
    if (item.formType === 2 || item.formType === 6) return []
    // formType=7 (TREE): 複数選択は [] 、単一選択は '' を初期値にする
    if (item.formType === 7) return item.isMultiple ? [] : ''
    if (item.formType === 8 && item.demographic === 'age') return '200001'
    return ''
  })()

  const selectPlaceholder = t('register_questionnaire.select_placeholder', '選択してください')

  return (
    <div>
      <Controller
        name={key}
        control={control}
        defaultValue={defaultValue}
        rules={{
          required: required ? t('register_questionnaire.answer_required', '回答は必須です') : false,
          validate: (value) => {
            // ZIP_CODE(formType=9): 7桁数字のみを許可
            if (item.formType === 9 && typeof value === 'string' && value.length > 0) {
              if (!/^\d{7}$/.test(value)) {
                return t('register_questionnaire.zip_code_error', '郵便番号はハイフンなし7桁の数字で入力してください')
              }
            }
            // AGE_SELECT(formType=10) + demographic=age: 0以上の整数のみ許可
            if (item.formType === 10 && item.demographic === 'age' && typeof value === 'string' && value.length > 0) {
              // 空白や指数表記・小数などを弾くため、まずは数字のみを許可する
              if (!/^\d+$/.test(value)) {
                return t('register_questionnaire.age_invalid', '年齢は0以上の整数で入力してください')
              }
              const num = Number.parseInt(value, 10)
              if (num < 0) {
                return t('register_questionnaire.age_invalid', '年齢は0以上の整数で入力してください')
              }
            }
            return true
          },
        }}
        render={({ field, fieldState }) => {
          switch (item.formType) {
            // RADIOBUTTON
            case 3:
              // demographic=area の場合は選択肢が多くなるため Select を使用
              if (item.demographic === 'area') {
                return (
                  <Select
                    label={label}
                    withAsterisk={required}
                    error={fieldState.error?.message}
                    placeholder={selectPlaceholder}
                    data={options.map((opt) => ({ label: opt, value: opt }))}
                    value={(field.value as string) || null}
                    onChange={(val) => field.onChange(val ?? '')}
                    allowDeselect={false}
                    inputMode="none"
                    styles={{ input: { fontSize: '16px' } as CSSProperties }}
                  />
                )
              }
              return (
                <MantineRadioGroup.Group
                  label={label}
                  withAsterisk={required}
                  error={fieldState.error?.message}
                  value={(field.value as string) ?? ''}
                  onChange={field.onChange}
                  styles={{ error: { marginTop: '8px' } }}
                >
                  <div className="flex flex-col gap-2 mt-2">
                    {options.map((opt) => (
                      <Radio key={opt} label={opt} value={opt} />
                    ))}
                  </div>
                </MantineRadioGroup.Group>
              )

            // CHECK_BOX
            case 2:
              // isMultiple=false の場合はラジオボタン相当の排他選択にする
              if (!item.isMultiple) {
                return (
                  <MantineRadioGroup.Group
                    label={label}
                    withAsterisk={required}
                    error={fieldState.error?.message}
                    value={((field.value as string[]) ?? [])[0] ?? ''}
                    onChange={(val) => field.onChange([val])}
                    styles={{ error: { marginTop: '8px' } }}
                  >
                    <div className="flex flex-col gap-2 mt-2">
                      {options.map((opt) => (
                        <Radio key={opt} label={opt} value={opt} />
                      ))}
                    </div>
                  </MantineRadioGroup.Group>
                )
              }
              return (
                <Checkbox.Group
                  label={label}
                  withAsterisk={required}
                  error={fieldState.error?.message}
                  value={(field.value as string[]) ?? []}
                  onChange={field.onChange}
                  styles={{ error: { marginTop: '8px' } }}
                >
                  <div className="flex flex-col gap-2 mt-2">
                    {options.map((opt) => (
                      <Checkbox
                        key={opt}
                        label={opt}
                        value={opt}
                        style={{ '--checkbox-size': '24px', '--checkbox-radius': '4px' } as CSSProperties}
                        classNames={{ input: checkboxStyles.input }}
                      />
                    ))}
                  </div>
                </Checkbox.Group>
              )

            // SELECT_BOX
            case 4:
              return (
                <Select
                  label={label}
                  withAsterisk={required}
                  error={fieldState.error?.message}
                  placeholder={selectPlaceholder}
                  data={options.map((opt) => ({ label: opt, value: opt }))}
                  value={(field.value as string) || null}
                  onChange={(val) => field.onChange(val ?? '')}
                  allowDeselect={false}
                  inputMode="none"
                  styles={{ input: { fontSize: '16px' } as CSSProperties }}
                />
              )

            // TEXT_AREA
            case 5:
              return (
                <Textarea
                  label={label}
                  withAsterisk={required}
                  error={fieldState.error?.message}
                  autosize
                  minRows={3}
                  value={(field.value as string) ?? ''}
                  onChange={(e) => field.onChange(e.currentTarget.value)}
                  onBlur={field.onBlur}
                  styles={{ input: { fontSize: '16px' } as CSSProperties }}
                />
              )

            // FILE_UPLOAD（バックエンド未実装のため暫定で非活性表示）
            case 6:
              return (
                <FileButton
                  label={label}
                  required={required}
                  multiple={item.isMultiple ?? false}
                  value={(field.value as string[]) ?? []}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  disabled
                  buttonLabel={t('register_questionnaire.file_upload_button', 'ファイルを選択')}
                  noFileText={t('register_questionnaire.file_upload_no_file', 'ファイルが選択されていません')}
                  clearLabel={t('register_questionnaire.file_upload_clear', 'クリア')}
                />
              )

            // TREE（市区町村選択）→ Select / MultiSelect に変更
            // 選択肢が多い場合に Tree ビューより Select の方が UX が良いため
            case 7:
              if (item.isMultiple) {
                return (
                  <MultiSelect
                    label={label}
                    withAsterisk={required}
                    error={fieldState.error?.message}
                    placeholder={selectPlaceholder}
                    data={options.map((opt) => ({ label: opt, value: opt }))}
                    value={(field.value as string[]) ?? []}
                    onChange={field.onChange}
                    inputMode="none"
                    styles={{ input: { fontSize: '16px' } as CSSProperties }}
                  />
                )
              }
              return (
                <Select
                  label={label}
                  withAsterisk={required}
                  error={fieldState.error?.message}
                  placeholder={selectPlaceholder}
                  data={options.map((opt) => ({ label: opt, value: opt }))}
                  value={(field.value as string) || null}
                  onChange={(val) => field.onChange(val ?? '')}
                  allowDeselect={false}
                  inputMode="none"
                  styles={{ input: { fontSize: '16px' } as CSSProperties }}
                />
              )

            // YEAR_MONTH + demographic=age → 年・月 SelectBox
            case 8:
              if (item.demographic === 'age') {
                return (
                  <QuestionnaireYearMonthField
                    label={label}
                    required={required}
                    value={(field.value as string) ?? ''}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )
              }
              // demographic が age 以外の YEAR_MONTH は TextInput で代替
              return (
                <TextInput
                  label={label}
                  withAsterisk={required}
                  error={fieldState.error?.message}
                  value={(field.value as string) ?? ''}
                  onChange={(e) => field.onChange(e.currentTarget.value)}
                  onBlur={field.onBlur}
                  styles={{ input: { fontSize: '16px' } as CSSProperties }}
                />
              )

            // ZIP_CODE（郵便番号: ハイフンなし7桁）
            case 9:
              return (
                <TextInput
                  label={t('register_questionnaire.zip_code_label', '郵便番号（ハイフンなし7桁）')}
                  withAsterisk={required}
                  error={fieldState.error?.message}
                  inputMode="numeric"
                  maxLength={7}
                  placeholder="例: 1234567"
                  value={(field.value as string) ?? ''}
                  onChange={(e) => field.onChange(e.currentTarget.value.replace(/\D/g, '').slice(0, 7))}
                  onBlur={field.onBlur}
                  styles={{ input: { fontSize: '16px' } as CSSProperties }}
                />
              )

            // AGE_SELECT (formType=10) + demographic=age → 数値直接入力
            case 10: {
              // demographic=age の場合は自由入力
              if (item.demographic === 'age') {
                return (
                  <TextInput
                    label={label}
                    withAsterisk={required}
                    error={fieldState.error?.message}
                    inputMode="numeric"
                    value={(field.value as string) ?? ''}
                    onChange={(e) => field.onChange(e.currentTarget.value)}
                    onBlur={field.onBlur}
                    styles={{ input: { fontSize: '16px' } as CSSProperties }}
                  />
                )
              }
              // value は回答値としてそのままバックエンドへ送信されるため日本語のまま保持し、
              // 表示ラベルのみ翻訳する。
              const ageOptions: { value: string; labelKey: string }[] = [
                { value: '10代未満', labelKey: 'under_10s' },
                { value: '10代', labelKey: '10s' },
                { value: '20代', labelKey: '20s' },
                { value: '30代', labelKey: '30s' },
                { value: '40代', labelKey: '40s' },
                { value: '50代', labelKey: '50s' },
                { value: '60代', labelKey: '60s' },
                { value: '70代', labelKey: '70s' },
                { value: '80代', labelKey: '80s' },
                { value: '90代以上', labelKey: '90s_or_over' },
              ]
              return (
                <Select
                  label={label}
                  withAsterisk={required}
                  error={fieldState.error?.message}
                  placeholder={selectPlaceholder}
                  data={ageOptions.map((opt) => ({
                    label: t(`register_questionnaire.age_range.${opt.labelKey}`, opt.value),
                    value: opt.value,
                  }))}
                  value={(field.value as string) || null}
                  onChange={(val) => field.onChange(val ?? '')}
                  allowDeselect={false}
                  inputMode="none"
                  styles={{ input: { fontSize: '16px' } as CSSProperties }}
                />
              )
            }

            // TEXT_BOX / default
            default:
              return (
                <TextInput
                  label={label}
                  withAsterisk={required}
                  error={fieldState.error?.message}
                  value={(field.value as string) ?? ''}
                  onChange={(e) => field.onChange(e.currentTarget.value)}
                  onBlur={field.onBlur}
                  styles={{ input: { fontSize: '16px' } as CSSProperties }}
                />
              )
          }
        }}
      />
    </div>
  )
})

QuestionnaireFormField.displayName = 'QuestionnaireFormField'
